import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Loader2,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import Nav from "../site/Nav";
import SEO from "../site/SEO";
import Footer from "../site/Footer";
import CheckoutPackages from "../site/CheckoutPackages";
import useScrollReveal from "../../hooks/useScrollReveal";
import { getAttribution } from "../../lib/attribution";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Service options must match backend whitelist exactly. The site uses a
 * stable slug → label map so any old URL with `?service=audit` etc. still
 * lands on the right pre-selected option. */
const SERVICE_OPTIONS = [
  "Revenue Leak Audit",
  "Website Build",
  "Lead Capture System",
  "Follow-Up + Booking System",
  "Revenue Conversion System",
  "Consultation",
  "Other",
];

const TIMELINE_OPTIONS = [
  "Immediately",
  "Within 7 Days",
  "Within 30 Days",
  "1–3 Months",
  "Just Researching",
];

const BUDGET_OPTIONS = [
  "Ready to Invest",
  "Needs Pricing First",
  "Budget Limited",
  "Not Sure Yet",
];

const CONTACT_METHOD_OPTIONS = [
  "Phone",
  "Text",
  "Email",
  "Instagram DM",
  "No Preference",
];

/* Map both legacy URL-param shorthand and old "offer" strings to the
 * canonical service_interest value, so every CTA across the site lands on
 * the right pre-selection without per-page changes. */
const SERVICE_ALIASES = {
  audit: "Revenue Leak Audit",
  "revenue-audit": "Revenue Leak Audit",
  "revenue-leak-audit": "Revenue Leak Audit",
  consultation: "Consultation",
  website: "Website Build",
  "website-build": "Website Build",
  starter: "Website Build",
  growth: "Website Build",
  premium: "Website Build",
  "lead-capture": "Lead Capture System",
  lead_capture: "Lead Capture System",
  "follow-up": "Follow-Up + Booking System",
  follow_up: "Follow-Up + Booking System",
  "revenue-conversion": "Revenue Conversion System",
  revenue_conversion: "Revenue Conversion System",
};

function resolveServiceFromQuery(searchParams) {
  const raw = (searchParams.get("service") || searchParams.get("package") || "")
    .trim()
    .toLowerCase();
  if (!raw) return SERVICE_OPTIONS[0];
  const aliased = SERVICE_ALIASES[raw];
  if (aliased && SERVICE_OPTIONS.includes(aliased)) return aliased;
  // Direct exact-match fallback (e.g. ?service=Revenue%20Leak%20Audit)
  const exact = SERVICE_OPTIONS.find((s) => s.toLowerCase() === raw);
  return exact || SERVICE_OPTIONS[0];
}

export default function IntakePage() {
  useScrollReveal();
  const [searchParams] = useSearchParams();

  const [form, setForm] = useState(() => ({
    full_name: "",
    business_name: "",
    email: "",
    phone: "",
    service_interest: resolveServiceFromQuery(searchParams),
    main_problem: "",
    timeline_to_start: "",
    budget_readiness: "",
    preferred_contact_method: "",
  }));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [calendlyUrl, setCalendlyUrl] = useState(null);
  const [intakeId, setIntakeId] = useState(null);

  // Re-resolve on URL change so /intake?service=… deep-links keep working.
  useEffect(() => {
    setForm((s) => ({
      ...s,
      service_interest: resolveServiceFromQuery(searchParams),
    }));
  }, [searchParams]);

  const update = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e.target.value }));

  const isValid = useMemo(
    () =>
      form.full_name.trim() &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()) &&
      form.phone.trim().length >= 7 &&
      form.service_interest &&
      form.main_problem.trim().length >= 5 &&
      form.timeline_to_start &&
      form.budget_readiness &&
      form.preferred_contact_method,
    [form],
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!isValid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const attribution = getAttribution();
      const payload = {
        full_name: form.full_name.trim(),
        business_name: form.business_name.trim() || null,
        email: form.email.trim(),
        phone: form.phone.trim(),
        service_interest: form.service_interest,
        main_problem: form.main_problem.trim(),
        timeline_to_start: form.timeline_to_start,
        budget_readiness: form.budget_readiness,
        preferred_contact_method: form.preferred_contact_method,
        utm_source: attribution.utm_source || null,
        utm_medium: attribution.utm_medium || null,
        utm_campaign: attribution.utm_campaign || null,
        utm_content: attribution.utm_content || null,
        utm_term: attribution.utm_term || null,
        gclid: attribution.gclid || null,
        fbclid: attribution.fbclid || null,
        referrer: attribution.referrer || null,
        landing_page: attribution.landing_page || null,
      };
      const response = await axios.post(`${API}/website-intake`, payload);
      setCalendlyUrl(response?.data?.calendly_url || null);
      setIntakeId(response?.data?.id || null);
      setSubmitted(true);
      requestAnimationFrame(() => {
        document
          .querySelector("[data-testid='intake-success']")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "We couldn't save your request. Please double-check your details and try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="bg-sand-100 text-ink min-h-screen"
      data-testid="intake-page"
    >
      <SEO
        page="/intake"
        title="Get in Touch · The Modern Exit"
        description="Tell us about your business and what you need built. The Modern Exit reviews every intake personally."
        path="/intake"
        noindex
      />
      <Nav />
      <main className="pt-24 sm:pt-28 md:pt-32 pb-16 px-5 sm:px-6 md:px-10">
        <div className="max-w-3xl mx-auto">
          <Link
            to="/"
            data-testid="intake-back"
            className="inline-flex items-center gap-2 text-[12.5px] text-ink-muted hover:text-clay transition-colors"
          >
            <ArrowLeft size={13} /> Back to home
          </Link>

          <div className="reveal mt-6">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay flex items-center gap-2">
              <Sparkles size={12} /> Intake · The Modern Exit
            </p>
            <h1
              data-kinetic
              data-testid="intake-headline"
              className="font-display mt-4 text-[2rem] sm:text-[2.4rem] md:text-[3rem] leading-[1.05] tracking-[-0.02em] font-semibold text-ink"
            >
              {submitted ? "Request received." : "Tell us about your business."}
            </h1>
            <p className="mt-4 text-[14.5px] sm:text-[15px] text-ink-muted max-w-xl leading-relaxed">
              {submitted
                ? "We'll review your information and follow up with the next step."
                : "Every CTA on the site lands here. Fill it out — we review every intake personally and follow up within one business day."}
            </p>
          </div>

          {submitted ? (
            <SuccessCard service={form.service_interest} calendlyUrl={calendlyUrl} intakeId={intakeId} />
          ) : (
            <form
              onSubmit={submit}
              className="mt-8 space-y-7"
              data-testid="intake-form"
              noValidate
            >
              {/* Identity */}
              <FieldGroup
                eyebrow="01 · Who you are"
                title="Tell us who's behind the business"
              >
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input
                    label="Full name"
                    required
                    value={form.full_name}
                    onChange={update("full_name")}
                    testid="intake-full-name"
                    placeholder="Jane Avery"
                    autoComplete="name"
                  />
                  <Input
                    label="Business name"
                    value={form.business_name}
                    onChange={update("business_name")}
                    testid="intake-business-name"
                    placeholder="Your business or brand name"
                    autoComplete="organization"
                  />
                  <Input
                    label="Email"
                    required
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    testid="intake-email"
                    placeholder="jane@averyplumbing.com"
                    autoComplete="email"
                  />
                  <Input
                    label="Phone"
                    required
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    testid="intake-phone"
                    placeholder="(214) 555-0123"
                    autoComplete="tel"
                  />
                  <Select
                    label="Preferred contact method"
                    required
                    value={form.preferred_contact_method}
                    onChange={update("preferred_contact_method")}
                    testid="intake-contact-method"
                    placeholder="Choose…"
                    options={CONTACT_METHOD_OPTIONS}
                  />
                </div>
              </FieldGroup>

              {/* What & Why */}
              <FieldGroup
                eyebrow="02 · What you need"
                title="What are you here for?"
              >
                <Select
                  label="Service interest"
                  required
                  value={form.service_interest}
                  onChange={update("service_interest")}
                  testid="intake-service-interest"
                  options={SERVICE_OPTIONS}
                />
                <Textarea
                  label="What's the main problem you're trying to solve?"
                  required
                  value={form.main_problem}
                  onChange={update("main_problem")}
                  testid="intake-main-problem"
                  placeholder="e.g. leads come in but nobody follows up fast enough; bookings drop after the first email; quotes go out 3-4 days late"
                  rows={4}
                />
              </FieldGroup>

              {/* When & Budget */}
              <FieldGroup
                eyebrow="03 · Timing & budget"
                title="A couple of details about timing"
              >
                <div className="grid sm:grid-cols-2 gap-4">
                  <Select
                    label="Timeline to start"
                    required
                    value={form.timeline_to_start}
                    onChange={update("timeline_to_start")}
                    testid="intake-timeline"
                    placeholder="Choose…"
                    options={TIMELINE_OPTIONS}
                  />
                  <Select
                    label="Budget readiness"
                    required
                    value={form.budget_readiness}
                    onChange={update("budget_readiness")}
                    testid="intake-budget"
                    placeholder="Choose…"
                    options={BUDGET_OPTIONS}
                  />
                </div>
              </FieldGroup>

              {error && (
                <div
                  data-testid="intake-error"
                  className="rounded-2xl border border-rust/40 bg-rust/5 px-4 py-3 text-[13px] text-rust"
                >
                  {error}
                </div>
              )}

              <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-2">
                <button
                  type="submit"
                  disabled={!isValid || submitting}
                  data-testid="intake-submit"
                  data-magnetic
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-ink-deep text-sand-100 px-6 py-3.5 text-[14px] font-medium hover:bg-rust transition-colors min-h-[48px] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={14} className="animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      Submit request <ArrowRight size={14} />
                    </>
                  )}
                </button>
                <p className="flex items-center gap-2 text-[12px] text-ink-muted">
                  <ShieldCheck size={12} className="text-olive" />
                  Reviewed personally — we follow up within one business day.
                </p>
              </div>
              <p
                data-testid="intake-consent"
                className="text-[11px] leading-relaxed text-ink-muted/80 max-w-xl"
              >
                By submitting this form, you agree that The Modern Exit may
                contact you by email or phone about your request.
              </p>
            </form>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function SuccessCard({ service, calendlyUrl, intakeId }) {
  const bookHref = intakeId ? `/book?ref=${encodeURIComponent(intakeId)}` : "/book";
  return (
    <section
      data-testid="intake-success"
      className="mt-8 rounded-3xl border border-olive/40 bg-olive/5 p-7 sm:p-9"
    >
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-olive flex items-center gap-2">
        <CheckCircle2 size={12} /> Received
      </p>
      <h2 className="font-display mt-3 text-[1.6rem] sm:text-2xl font-semibold text-ink leading-snug">
        Your request has been received.
      </h2>
      <p
        data-testid="intake-success-message"
        className="mt-4 text-[14.5px] text-ink leading-relaxed max-w-xl"
      >
        The Modern Exit will review your information and follow up with the
        next step.
      </p>
      {service && (
        <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.18em] text-clay">
          Service noted · {service}
        </p>
      )}

      <CheckoutPackages
        group="all"
        submissionId={intakeId}
        submissionKind="lead"
        title="Ready to move now? Pay securely to reserve your build."
      />

      {calendlyUrl && (
        <div className="mt-7 rounded-2xl border border-clay/30 bg-sand-50 p-5 sm:p-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay">
            Skip the wait
          </p>
          <p className="mt-2 text-[14.5px] text-ink leading-relaxed max-w-xl">
            Prefer to lock a time now? Pick a window that works for you and
            we'll meet you there.
          </p>
          <Link
            data-testid="intake-success-calendly"
            to={bookHref}
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-ink-deep text-sand-100 px-5 py-3 text-[13px] font-medium hover:bg-rust transition-colors min-h-[44px]"
          >
            Book a call <ArrowRight size={13} />
          </Link>
        </div>
      )}

      <Link
        to="/"
        className="mt-7 inline-flex items-center gap-2 text-[13px] text-ink-muted hover:text-clay transition-colors"
      >
        <ArrowLeft size={13} /> Back to home
      </Link>
    </section>
  );
}

/* Tiny presentational primitives — match the existing Modern Exit aesthetic. */
function FieldGroup({ eyebrow, title, children }) {
  return (
    <fieldset className="reveal rounded-3xl border border-sand-300 bg-sand-50 p-6 sm:p-7 space-y-5">
      <legend className="px-2 font-mono text-[10px] uppercase tracking-[0.2em] text-clay">
        {eyebrow}
      </legend>
      <h3 className="font-display text-lg sm:text-xl font-semibold text-ink leading-snug">
        {title}
      </h3>
      {children}
    </fieldset>
  );
}

function Label({ children }) {
  return (
    <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-muted">
      {children}
    </span>
  );
}

function Input({ label, testid, required, ...rest }) {
  return (
    <label className="block">
      <Label>
        {label}
        {required ? " *" : ""}
      </Label>
      <input
        {...rest}
        required={required}
        data-testid={testid}
        className="mt-1.5 w-full h-11 rounded-xl border border-sand-300 bg-sand-100 px-4 text-ink placeholder:text-ink-muted/50 focus:border-clay focus:outline-none focus:ring-2 focus:ring-clay/20 transition-colors"
      />
    </label>
  );
}

function Select({ label, testid, required, value, onChange, options, placeholder }) {
  return (
    <label className="block">
      <Label>
        {label}
        {required ? " *" : ""}
      </Label>
      <select
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="mt-1.5 w-full h-11 rounded-xl border border-sand-300 bg-sand-100 px-4 text-ink focus:border-clay focus:outline-none focus:ring-2 focus:ring-clay/20 transition-colors"
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </label>
  );
}

function Textarea({ label, testid, required, rows = 3, ...rest }) {
  return (
    <label className="block">
      <Label>
        {label}
        {required ? " *" : ""}
      </Label>
      <textarea
        {...rest}
        rows={rows}
        required={required}
        data-testid={testid}
        className="mt-1.5 w-full rounded-xl border border-sand-300 bg-sand-100 px-4 py-3 text-ink placeholder:text-ink-muted/50 focus:border-clay focus:outline-none focus:ring-2 focus:ring-clay/20 transition-colors leading-relaxed text-[14px]"
      />
    </label>
  );
}
